import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  SupportApi,
  extractCaseData,
  extractCaseListData,
  extractThreadData,
  extractThreadListData,
  extractMessageData,
  extractMessageListData,
  extractNoteData,
  extractNoteListData,
} from './supportApi';
import {
  SupportCase,
  CaseThread,
  CaseMessage,
  CaseNote,
  CaseHistoryEntry,
  DashboardData,
  DashboardMyData,
  InternalCreateSupportCaseRequest,
  UpdateSupportCaseRequest,
  CreateThreadRequest,
  InternalCreateMessageRequest,
  CreateNoteRequest,
  UpdateNoteRequest,
  SupportCaseFilters,
} from '../../types/support.types';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

// --- Query key factory ---

export const internalSupportKeys = {
  all: ['internalSupport'] as const,
  dashboard: () => [...internalSupportKeys.all, 'dashboard'] as const,
  dashboardMy: () => [...internalSupportKeys.all, 'dashboardMy'] as const,
  cases: () => [...internalSupportKeys.all, 'cases'] as const,
  caseList: (filters?: SupportCaseFilters) =>
    [...internalSupportKeys.cases(), 'list', filters] as const,
  caseDetails: () => [...internalSupportKeys.all, 'caseDetail'] as const,
  caseDetail: (caseId: string) =>
    [...internalSupportKeys.caseDetails(), caseId] as const,
  threads: (caseId: string) =>
    [...internalSupportKeys.all, 'threads', caseId] as const,
  messages: (caseId: string, threadId: string) =>
    [...internalSupportKeys.all, 'messages', caseId, threadId] as const,
  notes: (caseId: string) =>
    [...internalSupportKeys.all, 'notes', caseId] as const,
  history: (caseId: string) =>
    [...internalSupportKeys.all, 'history', caseId] as const,
};

// --- Query hooks ---

export function useInternalSupportDashboard() {
  return useQuery<DashboardData>({
    queryKey: internalSupportKeys.dashboard(),
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/support/dashboard`,
        { withCredentials: true },
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useInternalSupportDashboardMy() {
  return useQuery<DashboardMyData>({
    queryKey: internalSupportKeys.dashboardMy(),
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/support/dashboard/my`,
        { withCredentials: true },
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export interface SupportAgent {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
}

export function useInternalSupportAgents() {
  return useQuery<SupportAgent[]>({
    queryKey: [...internalSupportKeys.all, 'agents'] as const,
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/internal/support/agents`, {
        withCredentials: true,
      });
      return response.data.data || [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useInternalSupportCases(filters?: SupportCaseFilters) {
  return useQuery<SupportCase[]>({
    queryKey: internalSupportKeys.caseList(filters),
    queryFn: async () => {
      const response = await SupportApi.internalListCases(filters).run();
      return extractCaseListData(response);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useInternalSupportCase(caseId: string) {
  return useQuery<SupportCase>({
    queryKey: internalSupportKeys.caseDetail(caseId),
    queryFn: async () => {
      const response = await SupportApi.internalGetCase(caseId).run();
      return extractCaseData(response);
    },
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useInternalCaseThreads(caseId: string) {
  return useQuery<CaseThread[]>({
    queryKey: internalSupportKeys.threads(caseId),
    queryFn: async () => {
      const response = await SupportApi.internalListThreads(caseId).run();
      return extractThreadListData(response);
    },
    enabled: !!caseId,
    refetchInterval: 1000 * 30, // 30 seconds
  });
}

export function useInternalCaseMessages(
  caseId: string,
  threadId: string,
  opts?: { polling?: boolean },
) {
  return useQuery<CaseMessage[]>({
    queryKey: internalSupportKeys.messages(caseId, threadId),
    queryFn: async () => {
      const response = await SupportApi.internalListMessages(
        caseId,
        threadId,
      ).run();
      return extractMessageListData(response);
    },
    enabled: !!caseId && !!threadId,
    refetchInterval: opts?.polling ? 1000 * 10 : false, // 10 seconds when active
  });
}

export function useInternalCaseNotes(caseId: string) {
  return useQuery<CaseNote[]>({
    queryKey: internalSupportKeys.notes(caseId),
    queryFn: async () => {
      const response = await SupportApi.internalListNotes(caseId).run();
      return extractNoteListData(response);
    },
    enabled: !!caseId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useInternalCaseHistory(caseId: string) {
  return useQuery<CaseHistoryEntry[]>({
    queryKey: internalSupportKeys.history(caseId),
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/support/cases/${caseId}/history`,
        { withCredentials: true },
      );
      return response.data.data || response.data;
    },
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// --- Mutation hooks ---

export function useCreateSupportCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InternalCreateSupportCaseRequest) => {
      const response = await SupportApi.internalCreateCase(data).run();
      return extractCaseData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.cases(),
      });
    },
  });
}

export function useUpdateSupportCase(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateSupportCaseRequest) => {
      const response = await SupportApi.internalUpdateCase(caseId, data).run();
      return extractCaseData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.cases(),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.caseDetail(caseId),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.dashboard(),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.dashboardMy(),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.history(caseId),
      });
    },
  });
}

export function useCreateCaseThread(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateThreadRequest) => {
      const response = await SupportApi.internalCreateThread(
        caseId,
        data,
      ).run();
      return extractThreadData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.threads(caseId),
      });
    },
  });
}

export function useCreateCaseMessage(caseId: string, threadId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InternalCreateMessageRequest) => {
      const response = await SupportApi.internalCreateMessage(
        caseId,
        threadId,
        data,
      ).run();
      return extractMessageData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.messages(caseId, threadId),
      });
    },
  });
}

export function useUploadAttachment(caseId: string, threadId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      file,
    }: {
      messageId: string;
      file: File;
    }) => {
      // 1. POST metadata to get presigned upload URL
      const result = await SupportApi.internalUploadAttachment(
        caseId,
        messageId,
        {
          file_name: file.name,
          content_type: file.type || undefined,
          size_bytes: file.size,
        },
      );
      const uploadUrl = result.data.attributes.upload_url;

      // 2. PUT file bytes directly to S3
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.messages(caseId, threadId),
      });
    },
  });
}

export function useCreateCaseNote(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateNoteRequest) => {
      const response = await SupportApi.internalCreateNote(caseId, data).run();
      return extractNoteData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.notes(caseId),
      });
    },
  });
}

export function useUpdateCaseNote(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { noteId: string; data: UpdateNoteRequest }) => {
      const response = await SupportApi.internalUpdateNote(
        caseId,
        params.noteId,
        params.data,
      ).run();
      return extractNoteData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.notes(caseId),
      });
    },
  });
}

export function useDeleteCaseNote(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await SupportApi.internalDeleteNote(caseId, noteId).run();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.notes(caseId),
      });
    },
  });
}

export function useMergeSupportCase(sourceCaseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetCaseId: string) => {
      const response = await SupportApi.internalMergeCase(
        sourceCaseId,
        targetCaseId,
      ).run();
      return extractCaseData(response);
    },
    onSuccess: (_data, targetCaseId) => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.cases(),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.caseDetail(sourceCaseId),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.caseDetail(targetCaseId),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.dashboard(),
      });
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.dashboardMy(),
      });
    },
  });
}

export function useUploadNoteAttachment(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, file }: { noteId: string; file: File }) => {
      const result = await SupportApi.internalUploadNoteAttachment(
        caseId,
        noteId,
        {
          file_name: file.name,
          content_type: file.type || undefined,
          size_bytes: file.size,
        },
      );
      const uploadUrl = result.data.attributes.upload_url;

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalSupportKeys.notes(caseId),
      });
    },
  });
}
